import React, { useState, useEffect } from "react";
import {
    Modal, ModalBody, ModalFooter, ModalHeader,
    Input, Row, Col
} from "reactstrap";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import {
    listBuyersByParams, getBuyerById, verifyBuyer,
    unverifyBuyer, activateBuyer, deactivateBuyer
} from "../../api/buyer.api";

import StatusBadge from "../../components/StatusBadge";

const BuyerManagement = () => {
    const [data, setData] = useState([]);
    const [query, setQuery] = useState("");
    const [modal, setModal] = useState(false);
    const [buyer, setBuyer] = useState({});
    const [pageNo, setPageNo] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
        const [statusFilter, setStatusFilter] = useState("");

    // --- KEEPING EXISTING FUNCTIONS UNTOUCHED ---
    const fetchData = async () => {
        const skip = (pageNo - 1) * perPage;
        const res = await listBuyersByParams({ skip, per_page: perPage, match: query, verification_status: statusFilter });
        if (res.data.data.length > 0) {
            const result = res.data.data[0];
            setData(result.data);
            setTotalRows(result.count);
        } else { setData([]); }
    };

    useEffect(() => { fetchData(); }, [query, pageNo, perPage, statusFilter]);

    const handleView = async (id) => {
        const res = await getBuyerById(id);
        setBuyer(res.data.data);
        setModal(true);
    };

    const handleVerify = async () => { await verifyBuyer(buyer.id); toast.success("Buyer Verified"); setModal(false); fetchData(); };
    const handleUnverify = async () => { await unverifyBuyer(buyer.id); toast.success("Buyer Unverified"); setModal(false); fetchData(); };
    const handleActivate = async () => { await activateBuyer(buyer.id); toast.success("Buyer Activated"); setModal(false); fetchData(); };
    const handleDeactivate = async () => { await deactivateBuyer(buyer.id); toast.success("Buyer Deactivated"); setModal(false); fetchData(); };

    // --- DESIGN STYLES ---
    const customStyles = {
        headRow: { style: { borderBottom: "1px solid #e5e7eb" } },
        headCells: { style: { color: "#6b7280", fontWeight: "600", fontSize: "13px", padding: "18px" } },
        cells: { style: { padding: "18px", fontSize: "14px", color: "#112347" } },
    };

    const columns = [
        { name: "Business Name", selector: r => r.name, grow: 1.5, style: { fontWeight: "700" } },
        { name: "Email address", selector: r => r.email, grow: 1.5 },
        { name: "Phone number", selector: r => r.phone_number || "---" },
        { name: "Status", center: true, cell: r => <StatusBadge status={r.verification_status} /> },
        { name: "Joined", selector: r => r.created_at ? new Date(r.created_at).toLocaleDateString() : "2024-01-15" },
        {
            name: "Actions",
            right: true,
            cell: (row) => (
                <button className="btn btn-link p-0 text-muted" onClick={() => handleView(row.id)} style={{ fontSize: "18px" }}>
                    🖊️
                </button>
            )
        }
    ];

    return (
        <div style={{ backgroundColor: "#ffffff", padding: "clamp(15px, 3vw, 30px)", borderRadius: "12px", border: "1px solid #e5e7eb" }}>

            {/* HEADER SECTION */}
            <div className="mb-4">
                <h4 className="fw-bold m-0" style={{ color: "#112347" }}>Buyer Management</h4>
                <p className="text-muted small m-0">Verify and Moderate Platform User</p>
            </div>

            {/* SEARCH & FILTERS ROW */}
            <div className="d-flex flex-wrap gap-2 mb-4">
                <div style={{ position: "relative", flex: "1", minWidth: "200px", maxWidth: "400px" }}>
                    <span style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }}>🔍</span>
                    <Input
                        placeholder="Search User..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", paddingLeft: "40px", height: "45px" }}
                    />
                </div>

                <select
                    className="form-select w-auto"
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPageNo(1);
                    }}
                    style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", minWidth: "140px" }}
                >
                    <option value="">All Status</option>
                    <option value="VERIFIED">Verified</option>
                    <option value="PENDING">Pending</option>
                </select>
            </div>

            {/* DATA TABLE */}
            <div className="table-responsive border rounded-3 overflow-hidden">
                <DataTable
                    columns={columns}
                    data={data}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={setPageNo}
                    onChangeRowsPerPage={setPerPage}
                    customStyles={customStyles}
                />
            </div>

            {/* BUYER DETAILS OVERLAY - FIXED & SCROLLABLE */}
            <Modal
                isOpen={modal}
                toggle={() => setModal(false)}
                centered
                style={{ maxWidth: '750px', width: '100%' }}
            >
                <ModalHeader className="border-0 pb-0 pt-4 px-4" toggle={() => setModal(false)}>
                    <div className="d-flex align-items-center gap-2">
                        <h4 className="fw-bold m-0" style={{ color: "#112347" }}>{buyer.name}</h4>
                        <StatusBadge status={buyer.verification_status} />
                    </div>
                    <p className="text-muted small m-0 mt-1">Complete buyer profile details for review</p>
                </ModalHeader>

                <ModalBody className="px-4" style={{ height: '450px', overflowY: 'auto' }}>
                    <hr className="my-3 opacity-25" />

                    {/* USER INFORMATION */}
                    <div className="mb-4">
                        <h6 className="text-uppercase fw-bold text-muted mb-3" style={{ fontSize: '12px', letterSpacing: '1px' }}>User Information</h6>
                        <Row className="g-4">
                            <Col md="6">
                                <label className="text-muted small d-block mb-1">Full Name</label>
                                <span className="fw-bold text-dark">{buyer.name}</span>
                            </Col>
                            <Col md="6">
                                <label className="text-muted small d-block mb-1">Account Status</label>
                                <span className="fw-bold text-dark">{buyer.account_status}</span>
                            </Col>
                        </Row>
                    </div>

                    <hr className="my-4 opacity-25" />

                    {/* CONTACT DETAILS */}
                    <div className="mb-4">
                        <h6 className="text-uppercase fw-bold text-muted mb-3" style={{ fontSize: '12px', letterSpacing: '1px' }}>Contact Details</h6>
                        <Row className="g-3">
                            <Col md="6" className="d-flex align-items-center gap-2">
                                <span className="fs-5">✉️</span> <span className="fw-bold text-dark">{buyer.email}</span>
                            </Col>
                            <Col md="6" className="d-flex align-items-center gap-2">
                                <span className="fs-5">📞</span> <span className="fw-bold text-dark">{buyer.phone_number}</span>
                            </Col>
                        </Row>
                    </div>

                    <hr className="my-4 opacity-25" />

                    {/* DOCUMENTS */}
                    <div className="mb-3">
                        <h6 className="text-uppercase fw-bold text-muted mb-3" style={{ fontSize: '12px', letterSpacing: '1px' }}>Submitted ID Documents</h6>
                        <div className="p-3 border rounded d-flex flex-wrap justify-content-between align-items-center bg-light gap-2">
                            <div className="d-flex align-items-center gap-3"><span className="fs-3">📄</span> <span className="fw-bold" style={{ color: "#112347" }}>Profile Verification Doc</span></div>
                            <button className="btn btn-link text-dark fw-bold text-decoration-none">👁️ View</button>
                        </div>
                    </div>
                </ModalBody>

                <ModalFooter className="border-0 px-4 pb-4 pt-3 d-flex justify-content-between align-items-center bg-white flex-wrap gap-3">
                    <div className="text-muted small d-flex align-items-center gap-2">
                        <span className="fs-5">📅</span> Joined: {buyer.created_at ? new Date(buyer.created_at).toLocaleDateString() : "2024-01-15"}
                    </div>
                    <div className="d-flex gap-2">

                        {/* ❌ SHOW ONLY IF PENDING */}
                        {["PENDING", "UNVERIFIED"].includes(buyer.verification_status) && (
                        <button
                            className="btn btn-success fw-bold px-4"
                            style={{ backgroundColor: "#10b981", border: "none", borderRadius: "8px" }}
                            onClick={handleVerify}
                        >
                            ✓ Approve & Verify
                        </button>
                        )}

                        {/* ❌ SHOW ONLY IF VERIFIED */}
                        {buyer.verification_status === "VERIFIED" && (
                            <button
                            className="btn btn-outline-danger fw-bold px-4"
                            style={{ borderRadius: "8px", border: "1px solid #fee2e2" }}
                            onClick={handleUnverify}
                            >
                            ✖ Reject Registration
                            </button>
                        )}

                    </div>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default BuyerManagement;